import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
	DOCUMENT,
	inject,
	Injectable,
	InjectionToken,
	PLATFORM_ID,
	Renderer2,
} from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { TranslatedSlug } from '@valk-nx/compositions/ui-header/src/lib/header.interface';
import {
	ContentHotelInterface,
	HotelMainContactInterface,
} from '@valk-nx/core/lib/interfaces/hotels.interface';
import { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { GeneralHelper } from '@valk-nx/helpers/lib/general/general.helper';
import { removeTrailingSlash } from '@valk-nx/storyblok-helpers/src/lib/general/sitemap.helper';
import {
	PackageData,
	RoomData,
} from '@valk-nx/storyblok-types/src/lib/types/content.interface';
import {
	Link,
	PageMetaData,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

import {
	acceptedPaymentMethodSchemeOrg,
	amenitySchemeOrg,
	// areaServedSchemeOrg,
	brandSchemeOrg,
	businessFunctionSchemeOrg,
	collectionPageSchemeOrg,
	eligibleCustomerTypeSchemeOrg,
	// hotelContainedInPlaceSchemeOrg,
	imageSchemeOrg,
	mainEntityOfPageSchemeOrg,
	offersSchemeOrg,
	organizationSchemeOrg,
	postalAddressSchemeOrg,
	priceSpecificationSchemeOrg,
	quantitativeValueSchemeOrg,
} from './metadata.helper';

export interface MetaDataBatchInterface {
	meta?: {
		description: string;
		title: string;
	};
	images: PhotoInterface[];
}

export const HOST: InjectionToken<string> = new InjectionToken<string>('host');

@Injectable({ providedIn: 'root' })
export class MetadataService {
	private readonly metaTagService = inject(Meta);
	private readonly titleService = inject(Title);
	private readonly translate = inject(TranslateService);
	private readonly platformId = inject(PLATFORM_ID);
	readonly hostUrl = inject(HOST, { optional: true });
	private readonly router = inject(Router);
	private readonly document = inject<Document>(DOCUMENT);
	private readonly BRAND_URL = 'https://www.valk.com#VDV';

	constructor() {
		if (isPlatformBrowser(this.platformId) && !this.hostUrl) {
			this.hostUrl = `${window.location.protocol}//${window.location.host}`;
		}
	}

	updateMetadata(metadata: PageMetaData): void {
		const metatags: MetaDefinition[] =
			this.generateMetaDefinitions(metadata);

		this.removeMetaDefinitions();
		this.metaTagService.addTags([
			...metatags,
			{
				property: 'og:url',
				content: `${this.hostUrl}${this.router.url}`,
			},
			{
				name: 'robots',
				content: this.generateRobots(
					metadata.robotsNoIndex,
					metadata.robotsNoFollow,
				),
			},
		]);

		this.titleService.setTitle(metadata.metaTitle);
	}

	removeMetaDefinitions(): void {
		this.metaTagService.removeTag("name='title'");
		this.metaTagService.removeTag("property='og:title'");
		this.metaTagService.removeTag("name='description'");
		this.metaTagService.removeTag("property='og:description'");
		this.metaTagService.removeTag("property='og:type'");
		this.metaTagService.removeTag("property='og:url'");
		this.metaTagService.removeTag("name='robots'");
	}

	generateMetaDefinitions(metadata: PageMetaData): MetaDefinition[] {
		return [
			{ name: 'title', content: metadata.metaTitle },
			{ property: 'og:title', content: metadata.metaTitle },
			{ name: 'description', content: metadata.metaDescription },
			{
				property: 'og:description',
				content: metadata.metaDescription,
			},
			{ property: 'og:type', content: 'website' },
		];
	}

	generateRobots(noIndex: boolean, noFollow: boolean): string {
		let robots = noIndex ? 'noindex' : 'index';
		robots = noFollow ? `${robots}, nofollow` : `${robots}, follow`;
		return robots;
	}

	createLinkForCanonicalURL(canonical: Link | null) {
		let href = `${this.hostUrl}${this.router.url}`;

		if (canonical?.cached_url?.startsWith('http')) {
			href = canonical.cached_url;
		} else if (canonical?.cached_url && canonical?.id !== '') {
			const normalized = canonical.cached_url.startsWith('/')
				? canonical.cached_url
				: `/${canonical.cached_url}`;
			const route = normalized.replace(/\/home$/, '');
			href = `${this.hostUrl}${route}`;
		}

		const existingLink: HTMLLinkElement | null =
			this.document.querySelector('link[rel="canonical"]');

		const link: HTMLLinkElement =
			existingLink || this.document.createElement('link');

		link.setAttribute('rel', 'canonical');

		this.document.head.appendChild(link);
		link.setAttribute('href', removeTrailingSlash(href));
	}

	setHrefLang(translatedSlugs: TranslatedSlug[], defaultLanguage: string) {
		const defaultSlug = {
			path: '',
			...translatedSlugs.find(
				(slug) =>
					slug.lang.toLowerCase() === defaultLanguage.toLowerCase(),
			),
			lang: 'x-default',
		};

		[...translatedSlugs, defaultSlug].forEach((translatedSlug) => {
			const path = translatedSlug.path.endsWith('home')
				? translatedSlug.path.replace(/\/?home$/, '')
				: translatedSlug.path;

			const headDomElements = Array.from(
				this.document.head.children,
			) as unknown as HTMLLinkElement[];
			const alternateLink = headDomElements.find(
				(child: HTMLLinkElement) =>
					child.rel == 'alternate' &&
					child.hreflang.toLowerCase() ==
						translatedSlug.lang.toLowerCase(),
			);

			const href = removeTrailingSlash(`${this.hostUrl}/${path}`);

			if (alternateLink) {
				alternateLink.setAttribute('href', href);
			} else {
				const link = this.document.createElement('link');
				link.setAttribute('rel', 'alternate');
				this.document.head.appendChild(link);
				link.setAttribute(
					'hreflang',
					translatedSlug.lang.toLowerCase(),
				);
				link.setAttribute('href', href);
			}
		});
	}

	updateMetaDataBatch(
		data: MetaDataBatchInterface,
		robotsNoFollow = false,
		robotsNoIndex = false,
	) {
		this.updateMetadata({
			metaDescription: data.meta?.description ?? '',
			metaTitle: data.meta?.title ?? '',
			robotsNoFollow,
			robotsNoIndex,
		});
	}

	setDefaultSchema(renderer2: Renderer2) {
		const homepage = ['/', '/en', '/de', '/es', '/fr', '/nl'];
		if (!this.hostUrl) {
			return;
		}
		const domainName = new URL(this.hostUrl).hostname
			.replace(/^www\./, '')
			.split('.')
			.slice(0, -1)
			.join('.');

		if (
			isPlatformServer(this.platformId) &&
			homepage.includes(this.router.url)
		) {
			this.appendSchemaScript(renderer2, {
				'@context': 'https://schema.org',
				'@type': ['Organization'],
				'@id': `${this.hostUrl}/#Organization`,
				name: domainName,
				url: this.hostUrl,
				logo: 'https://www.valkdeals.nl/assets/logos/vdv-logo.svg',
				parentOrganization: {
					'@type': ['Brand', 'Organization'],
					'@id': 'https://www.valk.com#VDV',
					name: 'Van der Valk',
					url: 'https://www.valk.com',
					sameAs: [
						'https://g.co/kgs/kJaxLh',
						'https://www.wikidata.org/wiki/Q2802214',
						'https://nl.wikipedia.org/wiki/Van_der_Valk_(bedrijf)',
						'https://www.instagram.com/valkverrast',
						'https://www.facebook.com/VanderValkVerrast',
						'https://www.youtube.com/user/HotelsVanDerValk',
						'https://www.tiktok.com/@valkverrast',
						'https://nl.pinterest.com/vandervalkhotels/',
					],
				},
				foundingDate: '1939',
				contactPoint: {
					'@type': 'ContactPoint',
					telephone: '088 - 024 6301',
					contactType: 'Customer Service',
					hoursAvailable: {
						'@type': 'OpeningHoursSpecification',
						opens: '9:00',
						closes: '17:00',
						dayOfWeek: [
							'Monday',
							'Tuesday',
							'Wednesday',
							'Thursday',
							'Friday',
						],
					},
				},
				address: {
					'@type': 'PostalAddress',
					streetAddress: 'Stationsweg 91',
					addressLocality: 'Breukelen',
					postalCode: '3621 LK',
					addressCountry: 'NL',
				},
				email: 'support@valkservices.nl',
				brand: {
					'@id': 'https://www.valk.com/#VDV',
				},
			});
		}
	}

	setSchemaOrgHotel(renderer2: Renderer2, hotelData: ContentHotelInterface) {
		const homepage = ['/', '/en', '/de', '/es', '/fr', '/nl'];
		if (
			isPlatformServer(this.platformId) &&
			homepage.includes(this.router.url)
		) {
			const contact = hotelData.contacts.find(
				(contact) => contact.type === 'main',
			);

			this.appendSchemaScript(renderer2, {
				'@context': 'https://schema.org',
				'@type': ['Organization', 'LocalBusiness', 'Hotel'],
				'@id': `${this.hostUrl}/#Organization`,
				name: hotelData.name,
				url: this.hostUrl,
				logo: `${this.hostUrl}/assets/images/application-icons/icon-512x512.png`,
				openingHours: 'Mo, Tu, We, Th, Fr, Sa, Su 00:00 - 23:59',
				parentOrganization: {
					'@type': ['Brand', 'Organization'],
					'@id': this.BRAND_URL,
					name: 'Van der Valk',
					url: 'https://www.valk.com',
					sameAs: [
						'https://g.co/kgs/kJaxLh',
						'https://www.wikidata.org/wiki/Q2802214',
						'https://nl.wikipedia.org/wiki/Van_der_Valk_(bedrijf)',
						'https://www.instagram.com/valkverrast',
						'https://www.facebook.com/VanderValkVerrast',
						'https://www.youtube.com/user/HotelsVanDerValk',
						'https://www.tiktok.com/@valkverrast',
						'https://nl.pinterest.com/vandervalkhotels/',
					],
					foundingDate: '1929',
				},
				contactPoint: {
					'@type': 'ContactPoint',
					contactType: 'Customer Service',
					telephone: contact?.phone,
					hoursAvailable: {
						'@type': 'OpeningHoursSpecification',
						opens: '00:00',
						closes: '23:59',
						dayOfWeek: [
							'Monday',
							'Tuesday',
							'Wednesday',
							'Thursday',
							'Friday',
							'Saturday',
							'Sunday',
						],
					},
				},
				address: postalAddressSchemeOrg(hotelData.address),
				email: contact?.email,
				telephone: contact?.phone,
				brand: brandSchemeOrg(this.BRAND_URL),
			});
		}
	}

	setSchemaOrgRoomList(
		renderer2: Renderer2,
		name: string,
		roomData: RoomData[],
	) {
		if (isPlatformServer(this.platformId)) {
			const brand = this.BRAND_URL;
			const url = this.getCurrentUrl();
			const inLanguage = this.getCurrentLanguage();

			this.appendSchemaScript(renderer2, {
				'@context': 'https://schema.org',
				'@type': 'ItemList',
				'@id': `${url}#LP`,
				name,
				url,
				numberOfItems: roomData.length,
				itemListElement: roomData.map((room, index) => ({
					'@type': 'ListItem',
					position: index + 1,
					item: {
						'@type': ['HotelRoom', 'Product'],
						'@id': `${url}/${room.slug}#Room`,
						name: room.title,
						description: GeneralHelper.fromHtmlToString(
							room.description,
						),
						url: `${url}/${room.slug}`,
						containedInPlace: { '@id': url + '#Hotel' },
						...imageSchemeOrg(room.images, 256, 217),
						...amenitySchemeOrg(room.facilities),
						...offersSchemeOrg(room.price, brand),
						potentialAction: {
							'@type': 'ReserveAction',
							target: room.bookingToolRedirectUrl,
						},
						brand: brandSchemeOrg(brand),
						mainEntityOfPage: mainEntityOfPageSchemeOrg(
							`${url}/${room.slug}`,
							room.title,
							brand,
						),
					},
				})),
				mainEntityOfPage: mainEntityOfPageSchemeOrg(
					url,
					name,
					brand,
					inLanguage,
					`${url}#LP`,
				),
			});
		}
	}
	setSchemaOrgRoomDetail(
		renderer2: Renderer2,
		room: RoomData,
		_hotel: HotelMainContactInterface,
	) {
		if (isPlatformServer(this.platformId)) {
			const brand = this.BRAND_URL;
			const url = this.getCurrentUrl();
			const inLanguage = this.getCurrentLanguage();

			this.appendSchemaScript(renderer2, {
				'@context': 'https://schema.org',
				'@type': ['HotelRoom', 'Product'],
				'@id': `${url}#Room`,
				name: room.title,
				description: GeneralHelper.fromHtmlToString(room.description),
				url,
				accommodationCategory: 'Hotel',
				category: 'Hotelkamer',
				...imageSchemeOrg(room.images, 800, 640, true),
				...amenitySchemeOrg(room.facilities),
				...offersSchemeOrg(room.price, brand),
				// containedInPlace: hotelContainedInPlaceSchemeOrg(
				// 	`${this.hostUrl}/#Hotel`,
				// 	hotel.name,
				// 	this.hostUrl || '',
				// 	hotel.address,
				// 	'https://www.wikidata.org/wiki/Q2466573',
				// ),
				potentialAction: {
					'@type': 'ReserveAction',
				},
				floorSize: quantitativeValueSchemeOrg(
					room.surfaceArea / 10000,
					'MTK',
				),
				bed: {
					'@type': 'BedDetails',
					typeOfBed: room.facilities.sleepComfort,
				},
				occupancy: quantitativeValueSchemeOrg(
					room.occupancyRestrictions.maxPersons || 0,
					'C62',
				),
				brand: brandSchemeOrg(brand),
				mainEntityOfPage: mainEntityOfPageSchemeOrg(
					url,
					room.title,
					brand,
					inLanguage,
					`${url}#Room`,
				),
			});
		}
	}

	setSchemaOrgFaq(
		renderer2: Renderer2,
		name: string,
		description: string,
		faqs: { question: string; answer: string }[],
	) {
		if (isPlatformServer(this.platformId) && faqs.length > 0) {
			const url = this.getCurrentUrl();

			this.appendSchemaScript(renderer2, {
				'@context': 'https://schema.org',
				'@type': 'FAQPage',
				'@id': `${url}#FAQ`,
				url,
				name,
				description,
				mainEntity: faqs.map((faq) => ({
					'@type': 'Question',
					name: faq.question,
					acceptedAnswer: {
						'@type': 'Answer',
						text: faq.answer,
					},
				})),
				publisher: {
					'@type': 'Organization',
					'@id': `${this.hostUrl}/#Organization`,
				},
			});
		}
	}

	setSchemaOrgPackageList(
		renderer2: Renderer2,
		name: string,
		packageData: PackageData[],
		_hotel: HotelMainContactInterface,
	) {
		if (isPlatformServer(this.platformId)) {
			const brand = this.BRAND_URL;
			const url = this.getCurrentUrl();
			const inLanguage = this.getCurrentLanguage();

			this.appendSchemaScript(renderer2, {
				'@context': 'https://schema.org',
				'@type': 'ItemList',
				'@id': `${url}#LP`,
				name,
				url,
				numberOfItems: packageData.length,
				itemListElement: packageData.map((pkg, index) => ({
					'@type': 'ListItem',
					position: index + 1,
					item: {
						'@type': 'Offer',
						'@id': `${url}/${pkg.slug}#Package`,
						name: pkg.name,
						description: GeneralHelper.fromHtmlToString(
							pkg.description,
						),
						url: `${url}/${pkg.slug}`,
						...imageSchemeOrg(pkg.images, 283, 159),
						// ...areaServedSchemeOrg(
						// 	hotel.address.latitude,
						// 	hotel.address.longitude,
						// ),
						offeredBy: { '@id': brand },
						seller: { '@id': brand },
						...businessFunctionSchemeOrg('Sell'),
						...eligibleCustomerTypeSchemeOrg(),
						...priceSpecificationSchemeOrg(pkg.price, 'IE'),
						potentialAction: {
							'@type': 'ReserveAction',
							target: `${url}/${pkg.slug}`,
						},
						mainEntityOfPage: mainEntityOfPageSchemeOrg(
							`${url}/${pkg.slug}`,
							pkg.name,
							brand,
							inLanguage,
							`${url}/${pkg.slug}`,
						),
					},
				})),
				mainEntityOfPage: collectionPageSchemeOrg(
					url,
					this.titleService.getTitle(),
					brand,
					`${this.hostUrl}/#Hotel`,
					inLanguage,
				),
			});
		}
	}

	setSchemaOrgPackageDetail(
		renderer2: Renderer2,
		pkg: PackageData,
		hotel: HotelMainContactInterface,
	) {
		if (isPlatformServer(this.platformId)) {
			const brand = this.BRAND_URL;
			const url = this.getCurrentUrl();
			const inLanguage = this.getCurrentLanguage();

			this.appendSchemaScript(renderer2, {
				'@context': 'https://schema.org',
				'@type': 'Offer',
				'@id': `${url}#Package`,
				name: pkg.name,
				description: GeneralHelper.fromHtmlToString(pkg.description),
				url,
				availability: pkg.isExpired
					? 'https://schema.org/OutOfStock'
					: 'https://schema.org/InStock',
				validFrom: pkg.bookable.from,
				validThrough: pkg.bookable.to,
				...imageSchemeOrg(pkg.images, 714, 513, true),
				itemOffered: {
					'@type': 'Service',
					name: pkg.name,
					description: GeneralHelper.fromHtmlToString(
						pkg.description,
					),
				},
				// ...areaServedSchemeOrg(
				// 	hotel.address.latitude,
				// 	hotel.address.longitude,
				// 	undefined,
				// 	hotel.name,
				// 	this.hostUrl || '',
				// ),
				offeredBy: organizationSchemeOrg('Van der Valk', brand),
				seller: organizationSchemeOrg('Van der Valk', brand),
				...businessFunctionSchemeOrg('LeaseOut'),
				...eligibleCustomerTypeSchemeOrg(),
				sku: pkg.slug,
				...priceSpecificationSchemeOrg(pkg.price, 'C62'),
				acceptedPaymentMethod: acceptedPaymentMethodSchemeOrg(),
				potentialAction: {
					'@type': 'ReserveAction',
					target: url,
				},
				mainEntityOfPage: {
					...mainEntityOfPageSchemeOrg(
						`${url}`,
						`${pkg.name} | Van der Valk ${hotel.name}`,
						`${brand}#Organization`,
						inLanguage,
					),
				},
			});
		}
	}

	private appendSchemaScript(
		renderer2: Renderer2,
		schemaObject: object,
	): void {
		const script = renderer2.createElement('script');
		script.type = `application/ld+json`;
		script.text = JSON.stringify(schemaObject);
		renderer2.appendChild(this.document.head, script);
	}

	private getCurrentLanguage(): string | null {
		return (
			this.translate.getCurrentLang() || this.translate.getFallbackLang()
		);
	}

	private getCurrentUrl(): string {
		return `${this.hostUrl}${this.router.url}`;
	}
}
